/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import fs from 'fs';
import path from 'path';
import pico from 'picocolors';
import {Config, IOSProjectConfig} from '@react-native-community/cli-types';
import {
  logger,
  CLIError,
  link,
  startServerInNewWindow,
  findDevServerPort,
  cacheManager,
} from '@react-native-community/cli-tools';
import getArchitecture from '../../tools/getArchitecture';
import listDevices from '../../tools/listDevices';
import {promptForDeviceSelection} from '../../tools/prompts';
import {BuildFlags} from '../buildCommand/buildOptions';
import {buildProject} from '../buildCommand/buildProject';
import {getConfiguration} from '../buildCommand/getConfiguration';
import {getXcodeProjectAndDir} from '../buildCommand/getXcodeProjectAndDir';
import {getFallbackSimulator} from './getFallbackSimulator';
import {getPlatformInfo} from './getPlatformInfo';
import {
  printFoundDevices,
  matchingDevice,
  formattedDeviceName,
} from './matchingDevice';
import {runOnDevice} from './runOnDevice';
import {runOnSimulator} from './runOnSimulator';
import {BuilderCommand} from '../../types';
import {
  supportedPlatforms,
  resolvePods,
} from '@react-native-community/cli-config-apple';
import openApp from './openApp';

export interface FlagsT extends BuildFlags {
  simulator?: string;
  udid?: string;
  binaryPath?: string;
  listDevices?: boolean;
  packager?: boolean;
  port: number;
  terminal?: string;
}

function getPackageJson(root: string) {
  try {
    return require(path.join(root, 'package.json'));
  } catch {
    throw new CLIError(
      'No package.json found. Please make sure the file exists in the current folder.',
    );
  }
}

const createRun =
  ({platformName}: BuilderCommand) =>
  async (_: Array<string>, ctx: Config, args: FlagsT) => {
    // React Native docs assume platform is always ios/android
    link.setPlatform('ios');
    const platformConfig = ctx.project[platformName] as IOSProjectConfig;
    const {sdkNames, readableName: platformReadableName} =
      getPlatformInfo(platformName);

    if (
      platformConfig === undefined ||
      supportedPlatforms[platformName] === undefined
    ) {
      throw new CLIError(
        `Unable to find ${platformReadableName} platform config`,
      );
    }

    let {packager, port} = args;
    let installedPods = false;
    // check if pods need to be installed
    if (
      platformConfig.automaticPodsInstallation ||
      args.forcePods ||
      args.onlyPods
    ) {
      const isAppRunningNewArchitecture = platformConfig.sourceDir
        ? await getArchitecture(platformConfig.sourceDir)
        : undefined;

      await resolvePods(
        ctx.root,
        platformConfig.sourceDir,
        ctx.dependencies,
        platformName,
        ctx.reactNativePath,
        {
          forceInstall: args.forcePods || args.onlyPods,
          newArchEnabled: isAppRunningNewArchitecture,
        },
      );

      installedPods = true;
    }

    if (args.onlyPods) {
      return;
    }

    if (packager) {
      const {port: newPort, startPackager} = await findDevServerPort(
        port,
        ctx.root,
      );

      if (startPackager) {
        await startServerInNewWindow(
          newPort,
          ctx.root,
          ctx.reactNativePath,
          args.terminal,
        );
      }

      if (newPort !== port) {
        args.port = newPort;
      }
    }

    if (ctx.reactNativeVersion !== 'unknown') {
      link.setVersion(ctx.reactNativeVersion);
    }

    let {xcodeProject, sourceDir} = getXcodeProjectAndDir(
      platformConfig,
      platformName,
      installedPods,
    );

    process.chdir(sourceDir);

    if (args.binaryPath) {
      args.binaryPath = path.isAbsolute(args.binaryPath)
        ? args.binaryPath
        : path.join(ctx.root, args.binaryPath);

      if (!fs.existsSync(args.binaryPath)) {
        throw new CLIError(
          'binary-path was specified, but the file was not found.',
        );
      }
    }

    const {mode, scheme} = await getConfiguration(
      xcodeProject,
      sourceDir,
      args,
      platformName,
    );

    if (platformName === 'macos') {
      const buildOutput = await buildProject(
        xcodeProject,
        platformName,
        undefined,
        mode,
        scheme,
        args,
      );

      openApp({
        buildOutput,
        xcodeProject,
        mode,
        scheme,
        target: args.target,
        binaryPath: args.binaryPath,
      });

      return;
    }

    let devices = await listDevices(sdkNames);

    if (devices.length === 0) {
      return logger.error(
        `${platformReadableName} devices or simulators not detected. Install simulators via Xcode or connect a physical ${platformReadableName} device`,
      );
    }

    const packageJson = getPackageJson(ctx.root);

    const preferredDevice = cacheManager.get(
      packageJson.name,
      'lastUsedIOSDeviceId',
    );

    if (preferredDevice) {
      const preferredDeviceIndex = devices.findIndex(
        ({udid}) => udid === preferredDevice,
      );

      if (preferredDeviceIndex > -1) {
        const [device] = devices.splice(preferredDeviceIndex, 1);
        devices.unshift(device);
      }
    }

    const fallbackSimulator =
      platformName === 'ios' || platformName === 'tvos'
        ? getFallbackSimulator(args)
        : devices[0];

    if (args.listDevices || args.interactive) {
      if (args.device || args.udid) {
        logger.warn(
          `Both ${
            args.device ? 'device' : 'udid'
          } and "list-devices" parameters were passed to "run" command. We will list available devices and let you choose from one.`,
        );
      }

      const selectedDevice = await promptForDeviceSelection(devices);

      if (!selectedDevice) {
        throw new CLIError(
          `Failed to select device, please try to run app without ${
            args.listDevices ? 'list-devices' : 'interactive'
          } command.`,
        );
      } else {
        if (selectedDevice.udid !== preferredDevice) {
          cacheManager.set(
            packageJson.name,
            'lastUsedIOSDeviceId',
            selectedDevice.udid,
          );
        }
      }

      if (selectedDevice.type === 'simulator') {
        return runOnSimulator(
          xcodeProject,
          platformName,
          mode,
          scheme,
          args,
          selectedDevice,
        );
      } else {
        return runOnDevice(
          selectedDevice,
          platformName,
          mode,
          scheme,
          xcodeProject,
          args,
        );
      }
    }

    if (!args.device && !args.udid && !args.simulator) {
      const bootedSimulators = devices.filter(
        ({state, type}) => state === 'Booted' && type === 'simulator',
      );
      const connectedDevices = devices.filter(({type}) => type === 'device');

      const targetSimulator = bootedSimulators[0] ?? fallbackSimulator;

      if (bootedSimulators.length === 0) {
        logger.info(
          'No booted simulators found. Launching first available simulator...',
        );
      } else {
        logger.info(`Found booted ${targetSimulator.name}`);
      }

      if (connectedDevices.length > 0) {
        logger.info(
          `Ignoring connected ${
            connectedDevices.length === 1 ? 'device' : 'devices'
          } (${connectedDevices
            .map(({name}) => name)
            .join(
              ', ',
            )}). Pass --device or --udid to install on a physical device.`,
        );
      }

      return runOnSimulator(
        xcodeProject,
        platformName,
        mode,
        scheme,
        args,
        targetSimulator,
      );
    }

    if (args.device && args.udid) {
      return logger.error(
        'The `device` and `udid` options are mutually exclusive.',
      );
    }

    if (args.udid) {
      let device = devices.find((d) => d.udid === args.udid);
      if (!device) {
        // We may be able to recover from this for macCatalyst app starts,
        // but the general case is this is a problem and device list will help
        logger.warn(
          `Could not find a device with udid: "${pico.bold(
            args.udid,
          )}". ${printFoundDevices(devices)}`,
        );
      }
      if (device?.type === 'simulator') {
        return runOnSimulator(
          xcodeProject,
          platformName,
          mode,
          scheme,
          args,
          device,
        );
      } else {
        if (!device) {
          // On arm64 machines, the catalyst UDID returned by 'xcrun xctrace list devices' is not correct
          // xcodebuild will return an error indicating the UDID is unknown and offering a different one
          // you may obtain it by running xcodebuild with the UDID you think works then parse out the other one from the returned error:
          // CATALYST_DESTINATION=$(xcodebuild -workspace ios/rnfbdemo.xcworkspace -configuration Debug -scheme rnfbdemo -destination id=7153382A-C92B-5798-BEA3-D82D195F25F8 2>&1|grep macOS|grep Catalyst|head -1 |cut -d':' -f5 |cut -d' ' -f1)
          //
          // How to handle the incorrect catalyst UDID?
          // Assume if a UDID is specified, the user knows what they are doing.
          // Use the given UDID and force the type, so "catalyst" will launch the app correctly
          device = {name: 'unknown', udid: args.udid, type: 'catalyst'};
          logger.warn(
            'Running with provided udid anyway, and type "catalyst". \'xcodebuild\' command may return error.',
          );
        }
        return runOnDevice(
          device,
          platformName,
          mode,
          scheme,
          xcodeProject,
          args,
        );
      }
    } else if (args.device) {
      let device = matchingDevice(devices, args.device);

      if (!device) {
        const deviceByUdid = devices.find((d) => d.udid === args.device);
        if (!deviceByUdid) {
          return logger.error(
            `Could not find a physical device with name or unique device identifier: "${pico.bold(
              typeof args.device === 'string' ? args.device : 'true',
            )}". ${printFoundDevices(devices, 'device')}`,
          );
        }

        device = deviceByUdid;

        if (deviceByUdid.type === 'simulator') {
          return logger.error(
            `The device with udid: "${pico.bold(
              typeof args.device === 'string' ? args.device : 'true',
            )}" is a simulator. If you want to run on a simulator, use the "--simulator" flag instead.`,
          );
        }
      }

      if (device && device.type === 'simulator') {
        return logger.error(
          "`--device` flag is intended for physical devices. If you're trying to run on a simulator, use `--simulator` instead.",
        );
      }

      if (device && device.type === 'device') {
        return runOnDevice(
          device,
          platformName,
          mode,
          scheme,
          xcodeProject,
          args,
        );
      }
    } else if (args.simulator) {
      const matchedSimulator = devices.find(
        (d) =>
          d.type === 'simulator' &&
          (d.udid === args.simulator ||
            d.name === args.simulator ||
            formattedDeviceName(d) === args.simulator),
      );

      if (!matchedSimulator) {
        logger.warn(
          `Could not find a simulator with name or UDID: "${pico.bold(
            args.simulator,
          )}". ${printFoundDevices(devices, 'simulator')}`,
        );
        logger.info('Falling back to default simulator...');
      }

      return runOnSimulator(
        xcodeProject,
        platformName,
        mode,
        scheme,
        args,
        matchedSimulator ?? fallbackSimulator,
      );
    } else {
      runOnSimulator(
        xcodeProject,
        platformName,
        mode,
        scheme,
        args,
        fallbackSimulator,
      );
    }
  };

export default createRun;
